import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setMessage("Invalid verification link");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("verify-email", {
          body: { token },
        });

        if (error) throw error;

        if (data?.success) {
          setStatus("success");
          setMessage("Your email has been verified! Please log in to continue.");

          // Use redirectUrl from edge function (determined server-side)
          const redirectUrl = data.redirectUrl || "/producer-login?verified=true";

          setTimeout(() => {
            navigate(redirectUrl);
          }, 3000);
        } else {
          throw new Error(data?.error || "Verification failed");
        }
      } catch (error: unknown) {
        console.error(error);
        setStatus("error");
        setMessage(
          error instanceof Error ? error.message : "Failed to verify email. Please try again.",
        );
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
            <h2 className="mb-2 text-2xl font-bold">Verifying Email</h2>
            <p className="text-muted-foreground">
              Please wait while we verify your email address...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
            <h2 className="mb-2 text-2xl font-bold">Email Verified!</h2>
            <p className="mb-4 text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">Redirecting to login...</p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
            <h2 className="mb-2 text-2xl font-bold">Verification Failed</h2>
            <p className="mb-6 text-muted-foreground">{message}</p>
            <Button onClick={() => navigate("/producer-login")} className="w-full">
              Go to Login
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
